// contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is logged in on app start
    useEffect(() => {
        const checkAuthStatus = () => {
            try {
                const userData = localStorage.getItem('userData');

                if (userData) {
                    setUser(JSON.parse(userData));
                    setIsAuthenticated(true);
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                localStorage.removeItem('userData');
            } finally {
                setLoading(false);
            }
        };

        // Simulate loading delay for demo
        setTimeout(checkAuthStatus, 500);
    }, []);

    const login = async (credentials) => {
        setLoading(true);
        try {
            // Simulate loading delay for demo
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Simple demo authentication - just check if email and password are provided
            if (credentials.email === 'admin@tracktrace.com' && credentials.password === 'password') {
                const userData = {
                    id: 1,
                    name: 'Admin User',
                    email: 'admin@tracktrace.com',
                    role: 'Administrator',
                    avatar: null,
                    permissions: ['jobs:read', 'jobs:write', 'tracking:read', 'reports:read']
                };

                // Store user data for demo persistence
                localStorage.setItem('userData', JSON.stringify(userData));

                // Update state
                setUser(userData);
                setIsAuthenticated(true);

                return { success: true };
            } else {
                return { success: false, error: 'Invalid email or password' };
            }
        } catch (error) {
            console.error('Login failed:', error);
            return { success: false, error: 'An error occurred during login' };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            // Simulate loading delay for demo
            await new Promise(resolve => setTimeout(resolve, 500));

            // Clear storage
            localStorage.removeItem('userData');

            // Update state
            setUser(null);
            setIsAuthenticated(false);

            return { success: true };
        } catch (error) {
            console.error('Logout failed:', error);
            return { success: false, error: 'Logout failed' };
        }
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;